/**
 * The shared model behind every signed-container surface (Folders row, List and
 * Timeline rows, signature panel). Pure — no data access — so the derivation
 * rules are unit-testable and identical in all three views.
 */

import { canPreviewDocumentRow } from "@/utils/previewUtils";

/** Dokobit AES-policy check outcome; session records remain unknown until checked. */
export type SignatureValidity = "valid" | "invalid" | "unknown";

/**
 * One signer, from a Franklin signing session or a sanitised file inspection.
 *
 * Carries NO personal code: `signer_personal_code` holds national identity
 * numbers and is not granted to `authenticated`, so the app neither selects
 * nor renders it. A signer is identified by name and country.
 */
export interface ContainerSignature {
  id: number | string;
  signerName: string;
  signerCountry: string;
  /** Dokobit signing option: mobile | smartid | stationary. */
  method: string;
  /** Signature level reported by Dokobit: qes | aes | es. */
  signatureType: string | null;
  signedAt: string | null;
  timestampAt?: string | null;
  validity: SignatureValidity;
  warningCount?: number;
}

/**
 * Row/panel state for a signed container:
 * - `signed` — signatures exist and none failed the reported check; neutral blue.
 * - `issue` — at least one signature failed the reported check.
 * - `unverified` — no signature details are available; never a validity claim.
 * - `unsigned` — a completed inspection found no digital signatures.
 */
export type SignatureStatus = "signed" | "issue" | "unverified" | "unsigned";

/** Everything the UI needs about one signed container. */
export interface SignedContainerInfo {
  containerId: string;
  containerFilename: string;
  containerSize: number | null;
  signatures: ContainerSignature[];
  status: SignatureStatus;
  inspectionComplete?: boolean;
}

/**
 * A document enclosed in a container (an unzipped child of the .asice, or a
 * source that was signed into it). Carries the preview fields too — the
 * signature panel opens these documents itself, and they are deliberately kept
 * out of the document lists, so nothing else in the view holds their rows.
 */
export interface EnclosedDocument {
  id: string;
  filename: string;
  size: number | null;
  contentType: string | null;
  filePath: string | null;
  buildingId: string | null;
  createdAt: string | null;
  state: string | null;
}

/**
 * Whether the panel can OPEN an enclosed document rather than only download it:
 * it must be previewable (the same rule every document row uses) and Franklin
 * must hold the storage coordinates the preview loader needs. A source document
 * whose row was hard-deleted keeps its link row, so the fields are checked, not
 * assumed.
 */
export function canOpenEnclosedDocument(doc: EnclosedDocument): boolean {
  if (!doc.filePath || !doc.buildingId || !doc.createdAt) return false;
  return canPreviewDocumentRow({
    filename: doc.filename,
    content_type: doc.contentType,
    state: doc.state,
  });
}

/**
 * The status a row may claim for a container. `null` means "not known yet" —
 * while the lookup is loading, or after it failed, a row shows the neutral seal
 * rather than presenting "signed outside Franklin" as fact.
 */
export function summariseContainerLookup(
  isSuccess: boolean,
  info: SignedContainerInfo | undefined
): { status: SignatureStatus | null } {
  if (!isSuccess) return { status: null };
  return { status: info ? info.status : "unverified" };
}

/** Status for a container, from its signature records alone. */
export function deriveSignatureStatus(signatures: ContainerSignature[]): SignatureStatus {
  if (signatures.some((s) => s.validity === "invalid")) return "issue";
  if (signatures.length === 0) return "unverified";
  return "signed";
}
